import { useNavigate } from "react-router";

import ProgramBlockBlob1 from "@/../../hypetrain/static/img/exec/program-block-blob1.svg";
import ProgramBlockBlob2 from "@/../../hypetrain/static/img/exec/program-block-blob2.svg";
import ProgramBlockComponents from "@/../../hypetrain/static/img/exec/program-block-components.png";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { useAuth } from "@/components/lib/auth";
import { ProgramStatusBadge } from "@/components/pages_logged_in/program/components/ProgramStatus";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ProgramMiniType } from "@/graphql";

import { HomeBlock } from "./HomeBlock";

interface LeadProgramBlockProps {
  programs?: ProgramMiniType[];
}

export function LeadProgramBlock({ programs }: LeadProgramBlockProps) {
  const navigate = useNavigate();
  const isAdmin = isWorkspaceAdmin();
  const { currentWorkspace } = useAuth();

  const renderContent = (programList: ProgramMiniType[]) => {
    return (
      <div className="xs:p-5 xs:gap-3.5 flex flex-col md:gap-4 md:p-6">
        <div className="xs:text-lg font-semibold md:text-xl">
          Recent Programs
        </div>
        <div className="xs:gap-2.5 flex flex-col md:gap-3">
          {programList.map((program) => (
            <Card
              key={program.id}
              className="xs:p-3.5 md:p-4"
              onClick={() => {
                navigate(`/programs/${program.id}`);
              }}
            >
              <>
                <div className="xs:flex-col xs:gap-0.5 xs:text-sm flex justify-between md:text-base lg:flex-row lg:gap-6">
                  <div className="xs:line-clamp-2 flex-7 md:line-clamp-1">
                    {program.name}
                  </div>
                  <div className="flex flex-5 flex-row items-start justify-between sm:min-w-64">
                    <div>
                      {program.participantCount ? (
                        `${program.participantCount} participant${
                          program.participantCount > 1 ? "s" : ""
                        }`
                      ) : (
                        <span className="text-gray-400">None yet</span>
                      )}
                    </div>
                    <ProgramStatusBadge status={program.status} />
                  </div>
                </div>
              </>
            </Card>
          ))}
        </div>
        <div>
          <Button
            className="min-h-10 font-normal"
            intent="secondary"
            size="sm"
            onClick={() => navigate("/programs")}
          >
            See All
          </Button>
        </div>
      </div>
    );
  };

  return programs?.length ? (
    <HomeBlock
      key="program-home-block"
      title="Programs"
      description="Build training and education plans and assign them to your members."
      content={renderContent(programs)}
      onClick={() => {
        navigate("/programs");
      }}
    />
  ) : isAdmin ? (
    <HomeBlock
      key="program-home-block"
      title="Programs"
      description="Build training and education plans and assign them to your members."
      actionLabel={currentWorkspace.programsEnabled ? "Try Now" : "Book a Demo"}
      content={
        <div className="relative flex h-full overflow-hidden grayscale group-hover:grayscale-0 md:min-h-50">
          <div className="relative h-full flex-1">
            <EmbedSvg
              className="h-full translate-x-13 object-fill transition-all duration-300 ease-out group-hover:-translate-x-11"
              src={ProgramBlockBlob1}
            />
          </div>

          <div className="relative flex h-full flex-2 items-center justify-center">
            <img
              className="max-h-40 object-center transition-all duration-300 ease-out group-hover:scale-[125%]"
              src={ProgramBlockComponents}
            />
          </div>
          <div className="relative h-full flex-1">
            <EmbedSvg
              className="h-full -translate-x-13 object-fill transition-all duration-300 ease-out group-hover:translate-x-11"
              src={ProgramBlockBlob2}
            />
          </div>
        </div>
      }
      isEmpty={true}
      onClick={() => {
        if (!currentWorkspace.programsEnabled) {
          navigate("/schedule-demo");
        } else {
          navigate("/programs");
        }
      }}
    />
  ) : null;
}
